/* eslint no-nested-ternary: "off" */
import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import { UserContext } from "context/user";
import { useHistory } from "react-router-dom";
import Button from "components/Button";
import { toast } from "react-toastify";
import { getCropTypes } from "api/Field";
import { convertFile, createField, createFarm } from "api/Farm";
import { Upload, HardDrives, Flag, CaretDoubleRight } from "phosphor-react";
import CustomModal from "components/Modal";
import "./index.scss";
import UploadFileWarningModal from "../UploadFailedModal";

const UploadFile = ({ farmName, isOpen, onRequestClose, onClose, show }) => {
  const { token } = useContext(UserContext);
  const history = useHistory();
  const [minUploadFiles, setMinUploadFiles] = useState(0);
  const [maxUploadFiles, setMaxUploadFiles] = useState(0);
  const [fileType, setFileType] = useState("");
  const [acceptedFormat, setAcceptedFormat] = useState(
    ".shp, .dbf, .shx, .prj"
  );
  const [shp, setShp] = useState(null);
  const [dbf, setDbf] = useState(null);
  const [shx, setShx] = useState(null);
  const [prj, setPrj] = useState(null);
  const [kml, setKml] = useState(null);
  const [uploadFile, setUploadFile] = useState([]);
  const [steps] = useState(["uploadFile", "matchFields", "saveFile"]);
  const [currentStep, setCurrentStep] = useState("uploadFile");
  const [files, setFiles] = useState([]);

  // Match Fields
  const [fields, setFields] = useState({});
  const [fieldName, setField] = useState("");
  const [cropTypes, setCropTypes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [farmDetails, setFarmDetails] = useState(null);

  // Upload file failed
  const [isUploadFailed, setIsUploadFailed] = useState(false);
  const [failedFiles, setFailedFiles] = useState([]);
  const [successFiles, setSuccessFiles] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const formData = new FormData();
      const cropTypesData = await getCropTypes();
      setCropTypes(cropTypesData);
      if (files.length === 1) {
        formData.append("files", files[0]);
      } else {
        files.forEach((file) => {
          formData.append("files", file);
        });
      }

      try {
        if (formData.has("files")) {
          const convertedFile = await convertFile(formData, token);
          if (convertedFile.status !== 206) {
            const features = convertedFile.data.features.map((feature) => ({
              type: feature.type,
              geometry: feature.geometry,
              properties: feature.properties,
            }));
            const fieldParams = {
              type: convertedFile.data.type,
              features,
              bbox: convertedFile.data.bbox,
            };
            setFields(fieldParams);
          } else if (convertedFile.status === 206) {
            setSuccessFiles(convertedFile);
            setFailedFiles(convertedFile.data.invalid);
            setIsUploadFailed(true);
          }
        }
      } catch (e) {
        toast.error("Farm Upload failed. Contact support@datafarming.com.au");
      }
    };

    fetchData();
  }, [files, token]);

  const { getRootProps, getInputProps } = useDropzone({
    minFiles: minUploadFiles,
    maxFiles: maxUploadFiles,
    accept: acceptedFormat,
    onDrop: async (uploadFiles) => {
      if (!uploadFiles) return null;

      if (uploadFiles.length > 1) {
        const fileTypes = uploadFiles[0].path.substr(-3);
        if (
          fileTypes === "shp" ||
          fileTypes === "dbf" ||
          fileTypes === "prj" ||
          fileTypes === "shx"
        ) {
          setMinUploadFiles(4);
          setMaxUploadFiles(5);

          uploadFiles.map((file) => {
            const typeOfFile = file.path.substr(-3);
            if (typeOfFile === "shp") {
              setShp(file);
            }
            if (typeOfFile === "dbf") {
              setDbf(file);
            }
            if (typeOfFile === "shx") {
              setShx(file);
            }
            if (typeOfFile === "prj") {
              setPrj(file);
            }
            return file;
          });
          setUploadFile((oldFile) => [...oldFile, uploadFiles].flat());
        } else {
          toast.error("File type is not supported.");
        }
      }

      if (uploadFiles.length === 1) {
        const fileTypes = uploadFiles[0].path.substr(-3);
        if (fileTypes === "kml" || fileTypes === "kmz") {
          setMinUploadFiles(1);
          setMaxUploadFiles(1);
          setKml(uploadFiles[0]);
          setUploadFile(uploadFiles);
        }

        if (
          fileTypes === "shp" ||
          fileTypes === "dbf" ||
          fileTypes === "prj" ||
          fileTypes === "shx"
        ) {
          setMinUploadFiles(4);
          setMaxUploadFiles(5);
          uploadFiles.map((file) => {
            const typeOfFile = file.path.substr(-3);
            if (typeOfFile === "shp") {
              setShp(file);
            }
            if (typeOfFile === "dbf") {
              setDbf(file);
            }
            if (typeOfFile === "shx") {
              setShx(file);
            }
            if (typeOfFile === "prj") {
              setPrj(file);
            }
            setUploadFile((oldFile) => [...oldFile, uploadFiles].flat());
            return file;
          });
        } else if (fileTypes !== "kml" && fileTypes !== "kmz") {
          toast.error("File type is not supported.");
        }
      }
      return uploadFiles;
    },
  });

  const renderUploadedField = (items) => {
    const newfieldName = items.map((item) => {
      const columnName = Object.keys(item.properties);
      return columnName;
    });
    return newfieldName[0].map((col) => (
      <option key={col} value={col}>
        {col}
      </option>
    ));
  };

  const handleSelectFileType = (type) => {
    setFileType(type);

    if (type === "shapefile") {
      setAcceptedFormat(".shp, .dbf, .shx, .prj");
      setKml(null);
      setUploadFile([]);
    } else {
      setAcceptedFormat(".kmz,.kml");
      setShp(null);
      setShx(null);
      setPrj(null);
      setDbf(null);
      setUploadFile([]);
    }
  };

  const handleStep = () => {
    setCurrentStep(steps[steps.indexOf(currentStep) + 1]);
  };

  const GoBack = () => {
    setCurrentStep(steps[steps.indexOf(currentStep) - 1]);
    setIsUploadFailed(false);
    setFailedFiles([]);
  };

  const handleUploadFile = async (items) => {
    if (!items) {
      toast.error("No file to upload");
    } else {
      setFiles(items);
      handleStep();
    }
  };

  const handleNext = () => {
    if (uploadFile.length >= 3 && fileType === "shapefile") {
      handleUploadFile(uploadFile);
    } else if (uploadFile.length === 1 && fileType === "kml") {
      handleUploadFile(uploadFile);
    } else {
      toast.error("You need to upload other auxiliary files");
    }
  };

  const handleSkipFiles = () => {
    const features = successFiles.data.converted.features.map((feature) => ({
      type: feature.type,
      geometry: feature.geometry,
      properties: feature.properties,
    }));

    const fieldParams = {
      type: successFiles.data.converted.type,
      features,
      bbox: successFiles.data.converted?.bbox,
    };
    setFields(fieldParams);
    setIsUploadFailed(false);
  };

  const handleFieldChange = (value) => {
    setField(value);
  };

  const handleClose = () => {
    setIsUploadFailed(false);
  };

  const handleSaveFile = async () => {
    if (show === "New") {
      try {
        setIsSubmitting(true);
        const params = { data: { farm_name: farmName } };
        const newFarm = await createFarm(params, token);
        setFarmDetails(newFarm);
        const newMappedFields = [];
        fields.features.forEach((feature, index) => {
          debugger;
          newMappedFields.push({
            type: feature.type,
            geometry: feature.geometry,
            properties: {
              id: index,
              field_name: fieldName
                ? feature.properties[fieldName]
                  ? feature.properties[fieldName]
                  : `field ${index + 1}`
                : `field ${index + 1}`,
              field_crop_type: cropTypes.includes(
                feature?.properties?.crop_name
              )
                ? feature.properties.crop_name
                : "Other",
            },
          });
        });
        const newFields = {
          bbox: fields.bbox,
          type: fields.type,
          features: newMappedFields,
        };
        await createField(newFarm.properties.farm_id, newFields, token);
        handleStep();
        setIsUploadFailed(false);
        setFailedFiles([]);
        window.location.replace(`/farms/${newFarm.properties.farm_id}`);
      } catch (e) {
        setIsSubmitting(false);
        toast.error(e);
      }
    }

    if (show === "Add") {
      try {
        setIsSubmitting(true);
        const newMappedFields = [];
        fields.features.forEach((feature, index) => {
          newMappedFields.push({
            type: feature.type,
            geometry: feature.geometry,
            properties: {
              id: index,
              field_name: fieldName
                ? feature.properties[fieldName]
                  ? feature.properties[fieldName]
                  : `field ${index + 1}`
                : `field ${index + 1}`,
              field_crop_type: cropTypes.includes(
                feature?.properties?.crop_name
              )
                ? feature.properties.crop_name
                : "Other",
            },
          });
        });
        const newFields = {
          bbox: fields.bbox,
          type: fields.type,
          features: newMappedFields,
        };
        await createField(farmName.properties.farm_id, newFields, token);
        setIsUploadFailed(false);
        setFailedFiles([]);
        window.location.replace(`/farms/${farmName.properties.farm_id}`);
      } catch (e) {
        setIsSubmitting(false);
        toast.error(e?.response);
      }
    }
  };

  const handleRemoveFile = (i) => {
    const updatedFile = uploadFile.filter((file) => i !== file);
    setUploadFile(updatedFile);
    const typeOfFile = i.path.substr(-3);
    switch (typeOfFile) {
      case "shp":
        return setShp(null);
      case "dbf":
        return setDbf(null);
      case "shx":
        return setShx(null);
      case "prj":
        return setPrj(null);
      case "kml":
        return setKml(null);
      case "kmz":
        return setKml(null);
      default:
        return null;
    }
  };

  const handleCloseModal = () => {
    setCurrentStep("uploadFile");
    setFileType("");
    setMinUploadFiles(0);
    setMaxUploadFiles(0);
    setUploadFile([]);
    setShp(null);
    setDbf(null);
    setPrj(null);
    setKml(null);
    setShx(null);
    setFiles([]);
    setFields({});
    setIsUploadFailed(false);
    setSuccessFiles([]);
    setFailedFiles([]);
    onClose();
    onRequestClose();
  };

  const renderContents = () => {
    if (currentStep === "matchFields") {
      return (
        <>
          <div className="upload-files">
            <div className="title">
              Match your uploaded fields to ours
              <div className="description">
                Please match the attributes with the ones required. This is
                needed so the data from the Shapefile/KML can be correctly
                loaded in this system
              </div>
            </div>

            <div className="match-fields-container">
              <div className="field-title">
                <label>From File</label>
              </div>
              <CaretDoubleRight size={24} color="#292b33" weight="duotone" />
              <div className="field-title">
                <label>To DataFarming</label>
              </div>
            </div>
            <div className="match-fields-container">
              {fields?.features?.length > 0 && (
                <div className="form-row">
                  <select
                    className="form-input"
                    value={fieldName}
                    onChange={(event) => handleFieldChange(event.target.value)}
                  >
                    <option value="" selected>
                      Select Value for Field Name
                    </option>
                    {renderUploadedField(fields?.features)}
                  </select>
                </div>
              )}
              <CaretDoubleRight size={24} color="#292b33" weight="duotone" />
              {fields?.features?.length > 0 && (
                <div className="form-row">
                  <input
                    disabled
                    className="form-input"
                    placeholder="Field Name"
                  />
                </div>
              )}
            </div>
            <div className="action">
              <Button
                type="outlined"
                label="Go Back"
                onClick={() => GoBack()}
                size="medium"
                color="white"
                disabled={isSubmitting}
              />
              <Button
                type="solid"
                label="Next"
                onClick={() => handleSaveFile()}
                size="medium"
                color="blue"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </>
      );
    }

    if (currentStep === "saveFile") {
      return (
        <div className="upload-files">
          <div className="title -center">
            Your files have been successfully saved and uploaded to your farm
            <div className="description">
              You can now view your uploaded file to our mapping tool.
            </div>
          </div>
          <div className="action">
            <Button
              type="solid"
              label="Proceed to Crop Management"
              onClick={() =>
                history.push(`/farms/${farmDetails?.properties?.farm_id}`)
              }
              size="large"
              color="green"
            />
            <Button
              type="outlined"
              label="Exit & Close"
              onClick={() => window.location.replace("/farms")}
              size="medium"
              color="white"
            />
          </div>
        </div>
      );
    }

    return (
      <>
        <div className={`upload-files ${fileType === "" ? "-inactive" : ""}`}>
          <div className="title">Which file type are you going to upload?</div>
          <div className="grid-container" data-testid="grid-container">
            <div className="card-selection">
              <div
                className={`card ${
                  fileType === "shapefile" ? "-selected" : ""
                }`}
                onClick={() => handleSelectFileType("shapefile")}
                aria-hidden
              >
                .shp
              </div>
            </div>
            <div className="card-selection">
              <div
                className={`card ${fileType === "kml" ? "-selected" : ""}`}
                onClick={() => handleSelectFileType("kml")}
                aria-hidden
              >
                .kml or kmz
              </div>
            </div>
          </div>

          <section
            className={`dropzone-container ${
              fileType === "" ? "-inactive" : ""
            }`}
            data-testid="dropzone"
          >
            <div {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} />
              <p className="name">
                Drag & drop your shape files here or{" "}
                <span className="browse">click to browse</span>
              </p>
              <p className="note">
                If you have a .shp file, you must upload it with auxiliary
                files. <br /> All files must have the same file name.
              </p>
            </div>
          </section>
          {fileType === "shapefile" ? (
            <div
              className={`file-container ${fileType === "" ? "-inactive" : ""}`}
            >
              <div className="files">
                <div className="file-type">.shp</div>
                <div className="file-uploaded">
                  {shp?.name || "No .shp file found yet"}
                </div>
                {shp ? (
                  <p
                    className="remove"
                    onClick={() => handleRemoveFile(shp)}
                    aria-hidden
                  >
                    Remove
                  </p>
                ) : null}
              </div>
              <div className="files">
                <div className="file-type">.dbf</div>
                <div className="file-uploaded">
                  {dbf?.name || "No .dbf file found yet"}
                </div>
                {dbf ? (
                  <p
                    className="remove"
                    onClick={() => handleRemoveFile(dbf)}
                    aria-hidden
                  >
                    Remove
                  </p>
                ) : null}
              </div>
              <div className="files">
                <div className="file-type">.shx</div>
                <div className="file-uploaded">
                  {shx?.name || "No .shx file found yet"}
                </div>
                {shx ? (
                  <p
                    className="remove"
                    onClick={() => handleRemoveFile(shx)}
                    aria-hidden
                  >
                    Remove
                  </p>
                ) : null}
              </div>
              <div className="files">
                <div className="file-type">.prj</div>
                <div className="file-uploaded">
                  {prj?.name || "No .prj file found yet"}
                </div>
                <p className="optional">Optional</p>
                {prj ? (
                  <p
                    className="remove"
                    onClick={() => handleRemoveFile(prj)}
                    aria-hidden
                  >
                    Remove
                  </p>
                ) : (
                  ""
                )}
              </div>
            </div>
          ) : (
            <div
              className={`file-container ${fileType === "" ? "-inactive" : ""}`}
            >
              <div className="files">
                <div className="file-type">.kml or kmz</div>
                <div className="file-uploaded">
                  {kml?.name || "No .kml or kmz file found yet"}
                </div>
                {kml ? (
                  <p
                    className="remove"
                    onClick={() => handleRemoveFile(kml)}
                    aria-hidden
                  >
                    Remove
                  </p>
                ) : null}
              </div>
            </div>
          )}
          <div className="action">
            <Button
              type="solid"
              label="Next"
              onClick={() => handleNext()}
              size="medium"
              color="blue"
              disabled={fileType === "shapefile" ? !shp || !dbf || !shx : !kml}
            />
          </div>
        </div>
      </>
    );
  };
  return (
    <CustomModal
      isOpen={isOpen}
      onRequestClose={handleCloseModal}
      onClose={handleCloseModal}
      ariaHideApp={false}
      size="full"
      className="upload-modal"
    >
      <div className="modal-title">
        <div
          className={`header ${currentStep === "saveFile" ? "" : "-inactive"}`}
        >
          <div className="title">
            <Upload size={20} color="#292b33" />
            Upload File
            {currentStep === "uploadFile"
              ? ""
              : currentStep === "matchFields"
              ? ""
              : currentStep === "saveFile"
              ? ""
              : ""}
          </div>
          <div
            className={`title ${
              currentStep === "uploadFile"
                ? "-inactive"
                : currentStep === "matchFields"
                ? ""
                : currentStep === "saveFile"
                ? ""
                : ""
            }`}
          >
            <HardDrives size={20} color="#292b33" />
            Match Fields
          </div>
          <div
            className={`title ${
              currentStep === "uploadFile"
                ? "-inactive"
                : currentStep === "matchFields"
                ? "-inactive"
                : currentStep === "saveFile"
                ? ""
                : ""
            }`}
          >
            <Flag size={20} color="#292b33" />
            Save File
          </div>
        </div>
      </div>
      {renderContents()}
      {failedFiles ? (
        <UploadFileWarningModal
          isOpen={isUploadFailed}
          farms={failedFiles}
          onClose={handleClose}
          handleSkip={() => handleSkipFiles()}
        />
      ) : null}
    </CustomModal>
  );
};

UploadFile.propTypes = {
  farmName: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  show: PropTypes.string.isRequired,
};

export default UploadFile;
